// Global guard that shows the disabled-user modal on any authenticated route.
// Lives above the router so it is not tied to MainLayout.

import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@contexts/AuthContext';
import { useSocketEvent } from '../../hooks/useSocketEvent';
import { peopleService } from '../../services/peopleService';
import { authStorage } from '../../lib/storage';
import DisabledUserModal from './DisabledUserModal';

const DisabledUserGuard: React.FC = () =>
{
  const { logout } = useAuth();
  const [showDisabledUser, setShowDisabledUser] = useState(false);

  const checkUserEnabled = useCallback(async () =>
  {
    if (!authStorage.getAccessToken()) return;

    try
    {
      const people = await peopleService.getPeopleData();
      if (people && people.IsEnabled === false)
      {
        setShowDisabledUser(true);
      }
    }
    catch (error)
    {
      console.error('DisabledUserGuard: error checking user status:', error);
    }
  }, []);

  // Show the disabled-user message before logging out when the current user's
  // password is changed elsewhere.
  const handleChangePassword = useCallback(
    (data: { personId: number; token: string; invalidateToken: boolean }) =>
    {
      const currentPersonId = parseInt(localStorage.getItem('C2_personid') || '0', 10);
      const token = authStorage.getAccessToken();

      if (data.personId !== currentPersonId) return;

      const tokenChanged = token !== data.token && data.invalidateToken === false;
      const tokenInvalidated = token === data.token && data.invalidateToken === true;

      if (tokenChanged || tokenInvalidated)
      {
        setShowDisabledUser(true);
      }
    },
    [],
  );

  // Check on mount and whenever the server signals a logout/disable
  useEffect(() =>
  {
    checkUserEnabled();
  }, [checkUserEnabled]);

  useSocketEvent('socket:client:LogOutUser', checkUserEnabled);
  useSocketEvent('socket:client:changePassword', handleChangePassword);

  return <DisabledUserModal open={showDisabledUser} onLogout={logout} />;
};

export default DisabledUserGuard;
